import type { SimState } from '@md/engine';
import {
  MAGNET_RANGE_TILES,
  PARTICLE_DRAG_PER_SECOND,
  PARTICLE_EMIT_PER_SECOND,
  PARTICLE_LIFE_SECONDS,
  PARTICLE_MAX,
  PARTICLE_PULL_TILES_PER_S2,
  SHAKE_DAMPING_PER_SECOND,
  SHAKE_DEATH_TILES,
  SHAKE_REPEL_TILES,
} from '@md/shared';
import type { Graphics } from 'pixi.js';
import { createRandom } from './random';

/**
 * Particules métalliques et secousse d'écran (concept §9 : « feedback juteux »).
 *
 * Règle du projet : **le feedback est piloté par les états de simulation, jamais
 * l'inverse**. Ce module n'écrit rien dans le `SimState` ; il l'OBSERVE frame
 * après frame et en déduit ce qu'il faut afficher : de la limaille attirée par
 * les champs actifs, une secousse quand la répulsion claque ou qu'un robot est
 * détruit. La simulation reste pure et déterministe (spec §3).
 *
 * Le rendu vit en unités TUILES, comme le reste du graphe de scène.
 */

export interface ParticleFieldOptions {
  /** Graine du PRNG — l'aléa visuel reste reproductible d'une session à l'autre. */
  readonly seed?: number;
  readonly max?: number;
}

export interface ShakeOffset {
  readonly x: number;
  readonly y: number;
}

export interface ParticleField {
  /** Observe l'état courant et fait avancer les particules (par frame). */
  observe(state: SimState | null, dtMs: number): void;
  /** Dessine la limaille dans un `Graphics` en unités tuiles. */
  draw(target: Graphics): void;
  /** Décalage de caméra courant, en tuiles. */
  shake(): ShakeOffset;
  /** Oublie tout (changement de niveau). */
  reset(): void;
  count(): number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Vie restante, en secondes. */
  life: number;
  /** Vie initiale, pour l'opacité. */
  span: number;
  tint: number;
  /** Robot vers lequel la particule est attirée. */
  target: 0 | 1;
}

export function createParticleField(options: ParticleFieldOptions = {}): ParticleField {
  const random = createRandom(options.seed ?? 0x5eed);
  const max = options.max ?? PARTICLE_MAX;
  const particles: Particle[] = [];

  let shakeX = 0;
  let shakeY = 0;
  let emitCarry = 0;
  let lastTick = -1;
  let lastRepelling = false;
  let lastDeaths = -1;

  const impulse = (amount: number): void => {
    shakeX = (random() * 2 - 1) * amount;
    shakeY = (random() * 2 - 1) * amount;
  };

  const emit = (state: SimState, count: number): void => {
    for (let i = 0; i < count && particles.length < max; i += 1) {
      const index = random() < 0.5 ? 0 : 1;
      const player = state.players[index];
      if (!player.fieldActive || player.dead) {
        continue;
      }
      // La limaille apparaît sur le pourtour du champ et tombe vers le robot.
      const angle = random() * Math.PI * 2;
      const radius = MAGNET_RANGE_TILES * (0.55 + random() * 0.45);
      const cx = player.body.x + player.body.w / 2;
      const cy = player.body.y + player.body.h / 2;
      const span = PARTICLE_LIFE_SECONDS * (0.6 + random() * 0.8);
      particles.push({
        x: cx + Math.cos(angle) * radius,
        y: cy + Math.sin(angle) * radius,
        vx: 0,
        vy: 0,
        life: span,
        span,
        tint: player.character.tint,
        target: index,
      });
    }
  };

  return {
    observe(state: SimState | null, dtMs: number): void {
      const dt = Math.min(0.05, Math.max(0, dtMs) / 1000);
      // Amortissement de la secousse : indépendant de l'état, toujours appliqué.
      const damping = SHAKE_DAMPING_PER_SECOND ** dt;
      shakeX *= damping;
      shakeY *= damping;
      if (Math.abs(shakeX) < 1e-4) {
        shakeX = 0;
      }
      if (Math.abs(shakeY) < 1e-4) {
        shakeY = 0;
      }

      if (!state) {
        particles.length = 0;
        lastTick = -1;
        lastDeaths = -1;
        lastRepelling = false;
        return;
      }

      // Retour en arrière du tick = niveau rechargé : on repart à zéro sans
      // déclencher le moindre effet.
      if (state.tick < lastTick) {
        particles.length = 0;
        lastRepelling = false;
        lastDeaths = state.deaths;
      }
      lastTick = state.tick;

      // --- Déclencheurs lus dans l'état ------------------------------------
      const repelling =
        state.players[0].flags.repelling === 1 && state.players[0].flags.linked === 1;
      if (repelling && !lastRepelling) {
        impulse(SHAKE_REPEL_TILES);
      }
      lastRepelling = repelling;

      if (lastDeaths >= 0 && state.deaths > lastDeaths) {
        impulse(SHAKE_DEATH_TILES);
      }
      lastDeaths = state.deaths;

      // --- Émission ---------------------------------------------------------
      const activeFields = state.players.filter(
        (player) => player.fieldActive && !player.dead,
      ).length;
      if (activeFields > 0) {
        emitCarry += PARTICLE_EMIT_PER_SECOND * activeFields * dt;
        const count = Math.floor(emitCarry);
        emitCarry -= count;
        emit(state, count);
      } else {
        emitCarry = 0;
      }

      // --- Intégration ------------------------------------------------------
      const drag = PARTICLE_DRAG_PER_SECOND ** dt;
      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const particle = particles[i];
        if (!particle) {
          continue;
        }
        const player = state.players[particle.target];
        const cx = player.body.x + player.body.w / 2;
        const cy = player.body.y + player.body.h / 2;
        const dx = cx - particle.x;
        const dy = cy - particle.y;
        const distance = Math.hypot(dx, dy);
        if (distance > 1e-3) {
          // Répulsion : la limaille FUIT le robot au lieu d'y tomber.
          const sign = player.flags.repelling === 1 ? -1 : 1;
          const pull = (PARTICLE_PULL_TILES_PER_S2 * dt * sign) / Math.max(distance, 0.5);
          particle.vx += (dx / distance) * pull;
          particle.vy += (dy / distance) * pull;
        }
        particle.vx *= drag;
        particle.vy *= drag;
        particle.x += particle.vx * dt;
        particle.y += particle.vy * dt;
        particle.life -= dt;
        // Absorbée par le robot, ou éteinte.
        if (particle.life <= 0 || (distance < 0.35 && player.flags.repelling !== 1)) {
          particles.splice(i, 1);
        }
      }
    },

    draw(target: Graphics): void {
      target.clear();
      for (const particle of particles) {
        const alpha = Math.min(1, particle.life / particle.span) * 0.8;
        target.rect(particle.x - 0.045, particle.y - 0.045, 0.09, 0.09).fill({
          color: particle.tint,
          alpha,
        });
      }
    },

    shake(): ShakeOffset {
      return { x: shakeX, y: shakeY };
    },

    reset(): void {
      particles.length = 0;
      shakeX = 0;
      shakeY = 0;
      emitCarry = 0;
      lastTick = -1;
      lastDeaths = -1;
      lastRepelling = false;
    },

    count(): number {
      return particles.length;
    },
  };
}
